import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, LogOut, Loader2, GraduationCap, UserPlus, Trash2, ClipboardList, RefreshCw, Download, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  assigned: "border-slate-300 bg-slate-100 text-slate-500",
  in_progress: "border-amber/50 bg-amber/10 text-amber-700",
  completed: "border-moss/50 bg-moss/10 text-green-700",
};

const inputCls = "w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-cadmium/60 transition-colors";
const labelCls = "block text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1.5";

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="technical-card p-5">
    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className="mt-2 font-mono text-3xl font-semibold tabular-nums text-slate-900">{value}</p>
  </div>
);

export default function CompanyPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [empForm, setEmpForm] = useState({ name: "", email: "", password: "", community: "", training_format: "" });
  const [savingEmp, setSavingEmp] = useState(false);
  const [assignCourse, setAssignCourse] = useState("");
  const [assignPicks, setAssignPicks] = useState(new Set());
  const [assignDue, setAssignDue] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [smrForm, setSmrForm] = useState({ email: "", password: "" });
  const [savingSmr, setSavingSmr] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.get("/training/company/overview")
      .then((res) => {
        setData(res.data);
        if (res.data.smr?.email) setSmrForm((f) => ({ ...f, email: res.data.smr.email }));
      })
      .catch((err) => {
        const s = err.response?.status;
        if (s === 401) navigate("/login");
        else setError(err.response?.data?.detail || "Could not load your company view");
      });
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/login");
  };

  const createEmployee = async () => {
    setSavingEmp(true);
    try {
      const res = await api.post("/training/company/employees", empForm);
      toast.success(`Employee ${empForm.name} created`, {
        description: res.data.emailed
          ? `Sign-in details emailed to ${empForm.email}.`
          : "Account created but the credentials email could not be delivered — share the password directly.",
      });
      setEmpForm({ name: "", email: "", password: "", community: "", training_format: "" });
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Create failed");
    } finally {
      setSavingEmp(false);
    }
  };

  const deleteEmployee = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their course assignments and progress are deleted too.`)) return;
    try {
      await api.delete(`/training/company/employees/${id}`);
      toast.success("Employee removed");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Remove failed");
    }
  };

  const assign = async () => {
    if (!assignCourse || assignPicks.size === 0) { toast.error("Pick a course and at least one employee"); return; }
    setAssigning(true);
    try {
      const res = await api.post("/training/company/assign", {
        course_id: assignCourse, employee_ids: [...assignPicks], due_date: assignDue || null,
      });
      toast.success(`Assigned to ${res.data.assigned} employee(s)`, {
        description: [
          res.data.email_failed ? `${res.data.email_failed} email(s) could not be delivered.` : null,
          res.data.skipped_foreign ? `${res.data.skipped_foreign} selection(s) skipped — not in your company.` : null,
        ].filter(Boolean).join(" ") || undefined,
      });
      setAssignPicks(new Set());
      setAssignDue("");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const saveSmr = async () => {
    setSavingSmr(true);
    try {
      await api.put("/training/company/smr", smrForm);
      toast.success("ScheduleMyRent connection saved");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Save failed");
    } finally {
      setSavingSmr(false);
    }
  };

  const syncSmr = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/training/company/smr/sync");
      if (res.data.connected) toast.success("ScheduleMyRent synced");
      else toast.error("Sync failed", { description: res.data.error || "Unknown error" });
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const togglePick = (id) => {
    const next = new Set(assignPicks);
    next.has(id) ? next.delete(id) : next.add(id);
    setAssignPicks(next);
  };

  const smr = data?.smr;
  const k = smr?.latest?.kpis || {};

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB]" data-testid="company-portal">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={30} />
            <span className="font-cabinet text-lg font-bold tracking-wide text-slate-900">OPERA<span className="text-sky-600">360</span></span>
            <span className="rounded-sm border border-cadmium/40 bg-cadmium/10 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-sky-600">Company View</span>
          </Link>
          <button onClick={logout} data-testid="cp-logout" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {!data && !error && (
          <div className="flex items-center justify-center py-24"><Loader2 size={22} className="animate-spin text-slate-400" /></div>
        )}
        {error && (
          <div className="technical-card p-8 text-center" data-testid="cp-error">
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        )}
        {data && (
          <>
            <div className="flex items-center gap-2.5 mb-1">
              <Building2 size={16} className="text-sky-600" />
              <p className="text-xs font-mono uppercase tracking-[0.24em] text-slate-500">Company training overview</p>
            </div>
            <h1 className="font-cabinet text-3xl font-extrabold text-slate-900 mb-6" data-testid="cp-company-name">{data.company.name}</h1>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="cp-stats">
              <Stat label="Employees" value={data.stats.employees} testid="cp-stat-employees" />
              <Stat label="Open courses" value={data.stats.open_courses} testid="cp-stat-open" />
              <Stat label="Completed" value={data.stats.completed_courses} testid="cp-stat-completed" />
              <Stat label="Avg score" value={data.stats.avg_score != null ? `${data.stats.avg_score}%` : "—"} testid="cp-stat-avg" />
            </div>

            <div className="grid lg:grid-cols-2 gap-5 mb-8">
              {/* Add employee */}
              <div className="technical-card p-6" data-testid="cp-add-employee">
                <div className="flex items-center gap-2 mb-1">
                  <UserPlus size={14} className="text-sky-600" />
                  <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Add an employee</p>
                </div>
                <p className="text-[11px] text-slate-500 mb-4">They receive an email with their sign-in email and temporary password.</p>
                <div className="space-y-3">
                  <div><label className={labelCls}>Full name</label><input data-testid="cp-emp-name" className={inputCls} value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} placeholder="Jane Smith" /></div>
                  <div><label className={labelCls}>Email</label><input data-testid="cp-emp-email" type="email" className={inputCls} value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} placeholder="jane@company.com" /></div>
                  <div><label className={labelCls}>Temporary password (min 8 chars)</label><input data-testid="cp-emp-password" className={inputCls} value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} placeholder="••••••••" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Community / office</label><input data-testid="cp-emp-community" className={inputCls} value={empForm.community} onChange={(e) => setEmpForm({ ...empForm, community: e.target.value })} placeholder="Optional" /></div>
                    <div><label className={labelCls}>Training format</label>
                      <select data-testid="cp-emp-format" className={inputCls} value={empForm.training_format} onChange={(e) => setEmpForm({ ...empForm, training_format: e.target.value })}>
                        <option value="">Not set</option><option value="In person">In person</option><option value="Virtual">Virtual</option><option value="Video">Video</option>
                      </select>
                    </div>
                  </div>
                  <button data-testid="cp-emp-create" onClick={createEmployee} disabled={savingEmp} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-cadmium px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60">
                    {savingEmp ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Create Account
                  </button>
                </div>
              </div>

              {/* Assign course */}
              <div className="technical-card p-6" data-testid="cp-assign">
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardList size={14} className="text-sky-600" />
                  <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Assign a course</p>
                </div>
                <p className="text-[11px] text-slate-500 mb-4">Pick a course and employees; each gets an email with a due date.</p>
                <div className="space-y-3">
                  <div><label className={labelCls}>Course</label>
                    <select data-testid="cp-assign-course" className={inputCls} value={assignCourse} onChange={(e) => setAssignCourse(e.target.value)}>
                      <option value="">Select a course…</option>
                      {data.courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Employees</label>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="cp-assign-picks">
                      {data.employees.length === 0 && <p className="text-xs text-slate-400 px-1 py-2">Add employees first.</p>}
                      {data.employees.map((e) => (
                        <label key={e.id} className="flex items-center gap-2.5 rounded-sm px-2 py-1.5 hover:bg-slate-100 cursor-pointer" data-testid={`cp-pick-${e.id}`}>
                          <input type="checkbox" checked={assignPicks.has(e.id)} onChange={() => togglePick(e.id)} className="accent-[#2E9CFF]" />
                          <span className="text-sm text-slate-900">{e.name}</span>
                          <span className="text-[11px] text-slate-400">{e.email}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div><label className={labelCls}>Due date (optional)</label><input data-testid="cp-assign-due" type="date" className={inputCls} value={assignDue} onChange={(e) => setAssignDue(e.target.value)} /></div>
                  <button data-testid="cp-assign-submit" onClick={assign} disabled={assigning} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-cadmium px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60">
                    {assigning ? <Loader2 size={13} className="animate-spin" /> : <ClipboardList size={13} />} Assign Course
                  </button>
                </div>
              </div>
            </div>

            {/* ScheduleMyRent */}
            <div className="technical-card p-6 mb-8" data-testid="cp-smr">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <RefreshCw size={14} className="text-sky-600" />
                  <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">ScheduleMyRent — portfolio sync</p>
                </div>
                {smr?.configured && (
                  <button onClick={syncSmr} disabled={syncing} data-testid="cp-smr-sync" className="inline-flex items-center gap-1.5 rounded-md bg-cadmium px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60">
                    {syncing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} {syncing ? "Syncing…" : "Sync now"}
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mb-4">Connect your ScheduleMyRent landlord account to pull live occupancy and collections.</p>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div><label className={labelCls}>SMR email</label><input data-testid="cp-smr-email" type="email" className={inputCls} value={smrForm.email} onChange={(e) => setSmrForm({ ...smrForm, email: e.target.value })} placeholder="you@company.com" /></div>
                <div><label className={labelCls}>SMR password</label><input data-testid="cp-smr-password" type="password" className={inputCls} value={smrForm.password} onChange={(e) => setSmrForm({ ...smrForm, password: e.target.value })} placeholder="••••••••" /></div>
                <div className="flex items-end">
                  <button onClick={saveSmr} disabled={savingSmr} data-testid="cp-smr-save" className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-60">
                    {savingSmr ? <Loader2 size={11} className="animate-spin" /> : null} Save connection
                  </button>
                </div>
              </div>
              {smr?.latest && (
                <div data-testid="cp-smr-latest">
                  {smr.latest.error && <p className="text-xs text-red-600 mb-2" data-testid="cp-smr-error">{smr.latest.error}</p>}
                  {smr.latest.connected && (
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      <span className="font-mono text-slate-900"><span className="text-slate-500 text-[10px] uppercase tracking-widest mr-2">Occupied</span>{k.occupied_units ?? "—"}</span>
                      <span className="font-mono text-slate-900"><span className="text-slate-500 text-[10px] uppercase tracking-widest mr-2">Vacant</span>{k.vacant_units ?? "—"}</span>
                      <span className="font-mono text-slate-900"><span className="text-slate-500 text-[10px] uppercase tracking-widest mr-2">Vacancy</span>{k.vacancy_pct ?? "—"}%</span>
                      {k.current_month && <span className="font-mono text-slate-900"><span className="text-slate-500 text-[10px] uppercase tracking-widest mr-2">{k.current_month}</span>${(k.rent_received_current ?? 0).toLocaleString()} collected</span>}
                      <span className="text-[10px] font-mono text-slate-400 self-center">synced {(smr.latest.synced_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                  )}
                </div>
              )}
              {!smr?.configured && <p className="text-[11px] text-slate-400" data-testid="cp-smr-empty">No connection saved yet — enter your ScheduleMyRent credentials above, then sync.</p>}
            </div>

            {/* Progress table */}
            <div className="technical-card overflow-hidden mb-8" data-testid="cp-progress-table">
              <div className="flex items-center gap-2 px-5 pt-5 pb-3">
                <GraduationCap size={14} className="text-sky-600" />
                <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Employee scores & progress</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/75 text-left">
                      {["Employee", "Course", "Status", "Progress", "Best Score", "Records", "Due"].map((h) => (
                        <th key={h} className="px-5 py-3 text-[10px] font-mono uppercase tracking-[0.18em] font-semibold text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.progress.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400" data-testid="cp-empty">
                        No courses assigned yet — assign one above.
                      </td></tr>
                    )}
                    {data.progress.map((a) => (
                      <tr key={a.id} className="border-b border-slate-100" data-testid={`cp-row-${a.id}`}>
                        <td className="px-5 py-3.5">
                          <p className="text-slate-900 font-medium">{a.employee_name || "—"}</p>
                          <p className="text-[11px] text-slate-400">{a.employee_email}</p>
                        </td>
                        <td className="px-5 py-3.5 text-slate-500">{a.course_title}</td>
                        <td className="px-5 py-3.5">
                          <span className={`rounded-sm border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[a.status] || STATUS_BADGE.assigned}`}>{a.status.replace("_", " ")}</span>
                        </td>
                        <td className="px-5 py-3.5 font-mono tabular-nums text-slate-500">{a.progress_pct}%</td>
                        <td className="px-5 py-3.5 font-mono tabular-nums text-slate-900">{a.best_score != null ? `${a.best_score}%` : "—"}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            {a.passed && (
                              <a href={`${BACKEND}/api/training/certificate/${a.id}.pdf?style=print`} target="_blank" rel="noreferrer" title="Certificate (PDF)" data-testid={`cp-cert-${a.id}`} className="text-sky-600 hover:text-slate-900 transition-colors"><Download size={14} /></a>
                            )}
                            {a.attempts > 0 && (
                              <a href={`${BACKEND}/api/training/exam-sheet/${a.id}.pdf`} target="_blank" rel="noreferrer" title="Scored exam sheet (PDF)" data-testid={`cp-sheet-${a.id}`} className="text-sky-600 hover:text-slate-900 transition-colors"><FileText size={14} /></a>
                            )}
                            {!a.passed && a.attempts === 0 && <span className="text-slate-300 font-mono text-xs">—</span>}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-500">
                          {a.due_date || "—"}
                          {a.overdue && <span className="ml-1.5 rounded-sm border border-amber/50 bg-amber/10 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-amber-700">overdue</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Employees */}
            <div className="technical-card p-6" data-testid="cp-employees">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-sky-600" />
                  <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Your employees ({data.employees.length})</p>
                </div>
                <a
                  href={`${BACKEND}/api/training/company/transcripts.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  title="Export every employee transcript as one PDF"
                  data-testid="cp-export-transcripts"
                  className="inline-flex items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] font-mono font-semibold uppercase tracking-wider text-sky-700 hover:bg-sky-100 transition-colors"
                >
                  <GraduationCap size={13} /> Export all transcripts
                </a>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {data.employees.map((e) => (
                  <div key={e.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`cp-emp-${e.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{e.name}</p>
                      <p className="text-[11px] text-slate-400">{e.email}</p>
                      {(e.community || e.training_format) && (
                        <p className="text-[10px] font-mono text-slate-400 mt-0.5">{[e.community, e.training_format].filter(Boolean).join(" · ")}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={`${BACKEND}/api/training/employees/${e.id}/transcript.pdf`} target="_blank" rel="noreferrer" title="Training transcript (PDF)" data-testid={`cp-transcript-${e.id}`} className="text-slate-400 hover:text-sky-600 transition-colors">
                        <GraduationCap size={13} />
                      </a>
                      <button onClick={() => deleteEmployee(e.id, e.name)} title="Remove employee" data-testid={`cp-emp-delete-${e.id}`} className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
